import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { getAPIBaseURL, getAdmin } from "../../services/helpers"
import { getData, postData, updateData, deleteData } from "../../services/request"

const AdminUserDetails = () => {
  const { adminId } = useParams()
  const navigate = useNavigate()
  const currentAdmin = getAdmin()
  const isSuperAdmin = currentAdmin?.role === 'SUPER_ADMIN'
  const isOwnAccount = currentAdmin?._id === adminId || currentAdmin?.id === adminId

  const [admin, setAdmin] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")

  // Edit form state
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState("")
  const [username, setUsername] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [role, setRole] = useState("ADMIN")
  const [status, setStatus] = useState(1)

  // Change password state
  const [changingPassword, setChangingPassword] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordError, setPasswordError] = useState("")

  // Action states
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fetchAdmin = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/admins/${adminId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        const data = res.result.data?.data || null
        setAdmin(data)
        if (data) {
          setName(data.name || "")
          setUsername(data.username || "")
          setPhoneNumber(String(data.phoneNumber || ""))
          setRole(data.role || "ADMIN")
          setStatus(data.status !== undefined ? data.status : 1)
        }
      } else {
        setError(res.result.data?.message || 'Admin not found')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchAdmin()
  }, [adminId])

  const handleEdit = () => {
    setEditing(true)
    setError("")
    setGenMsg("")
  }

  const handleCancelEdit = () => {
    setEditing(false)
    if (admin) {
      setName(admin.name || "")
      setUsername(admin.username || "")
      setPhoneNumber(String(admin.phoneNumber || ""))
      setRole(admin.role || "ADMIN")
      setStatus(admin.status !== undefined ? admin.status : 1)
    }
  }

  const handleUpdate = async () => {
    setError("")
    setGenMsg("")
    if (!name || name.trim().length < 2) {
      setError("Name must be at least 2 characters")
      return
    }
    if (!username || username.trim().length < 2) {
      setError("Username must be at least 2 characters")
      return
    }
    if (!phoneNumber || phoneNumber.length !== 11 || !/^\d+$/.test(phoneNumber)) {
      setError("Phone number must be exactly 11 digits")
      return
    }

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/admins`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('adminId', adminId)
    params.append('name', name.trim())
    params.append('username', username.trim())
    params.append('phoneNumber', phoneNumber.trim())
    params.append('role', role)
    params.append('status', status.toString())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg("Admin updated successfully")
        setEditing(false)
        fetchAdmin()
      } else {
        setError(res.result.data?.message || 'Could not update admin')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleChangePassword = async () => {
    setPasswordError("")
    if (!newPassword || newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters")
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match")
      return
    }

    setChangingPassword(true)
    const url = `${getAPIBaseURL()}/v1/admins/change-password`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('adminId', adminId)
    params.append('newPassword', newPassword)

    const res = await updateData(url, headers, params.toString())
    setChangingPassword(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg("Password changed successfully")
        setNewPassword("")
        setConfirmPassword("")
      } else {
        setPasswordError(res.result.data?.message || 'Could not change password')
      }
    } else {
      setPasswordError(res.result?.data?.message || 'Network error')
    }
  }

  const handleToggleStatus = async () => {
    setError("")
    setGenMsg("")
    const newStatus = admin.status === 1 ? 0 : 1

    setSubmitting(true)
    const url = `${getAPIBaseURL()}/v1/admins/status`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('adminId', adminId)
    params.append('status', newStatus.toString())

    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg(`Admin ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`)
        fetchAdmin()
      } else {
        setError(res.result.data?.message || 'Could not update status')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete admin "${admin?.name}"? This action cannot be undone.`)) {
      return
    }

    setError("")
    setGenMsg("")
    setDeleting(true)
    const url = `${getAPIBaseURL()}/v1/admins`
    const res = await deleteData(url, {}, { adminId: adminId })
    setDeleting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        navigate("/admin/app/system-users")
      } else {
        setError(res.result.data?.message || 'Could not delete admin')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const infoRow = (label, value) => (
    <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
      <div className="col-span-1 font-medium text-gray-600">{label}</div>
      <div className="col-span-2 text-gray-900">{value || '—'}</div>
    </div>
  )

  if (!isSuperAdmin) {
    return (
      <section className="mx-auto max-w-4xl space-y-4">
        <Link to="/admin/app/system-users" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
          ← Back to system users
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          You do not have permission to view admin details. Only SUPER_ADMIN can access this page.
        </div>
      </section>
    )
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <Link to="/admin/app/system-users" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
        ← Back to system users
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h1 className="text-2xl font-bold text-gray-900">Admin Details</h1>
          <p className="mt-1 text-sm text-gray-600">Admin ID: {adminId}</p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error && !admin ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
          ) : !admin ? (
            <div className="text-sm text-gray-500">Admin not found.</div>
          ) : (
            <>
              {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
              ) : null}
              {genMsg ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
              ) : null}

              {editing ? (
                <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Edit Admin Details</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Username</label>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Phone Number</label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                        maxLength={11}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Role</label>
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="ADMIN">ADMIN</option>
                        <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">Status</label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(Number(e.target.value))}
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleUpdate}
                      disabled={submitting}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={submitting}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-100">
                  {infoRow('Name', admin.name)}
                  {infoRow('Username', admin.username)}
                  {infoRow('Phone Number', admin.phoneNumber)}
                  {infoRow('Role', (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      admin.role === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {admin.role}
                    </span>
                  ))}
                  {infoRow('Status', (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      admin.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {admin.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  ))}
                  {infoRow('Created At', formatDate(admin.createdAt))}
                  {infoRow('Updated At', formatDate(admin.updatedAt))}
                </div>
              )}

              {/* Change Password Section */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Change Password</h3>
                {passwordError ? (
                  <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-700">{passwordError}</div>
                ) : null}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-900">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-900">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !newPassword || !confirmPassword}
                  className="mt-3 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {!editing && (
                  <button
                    onClick={handleEdit}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Edit Details
                  </button>
                )}
                <button
                  onClick={handleToggleStatus}
                  disabled={submitting || isOwnAccount}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${
                    admin.status === 1
                      ? 'border border-red-200 bg-white text-red-600 hover:bg-red-50'
                      : 'border border-green-200 bg-white text-green-600 hover:bg-green-50'
                  }`}
                >
                  {submitting ? 'Updating...' : admin.status === 1 ? 'Deactivate' : 'Activate'}
                </button>
                {!isOwnAccount && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {deleting ? 'Deleting...' : 'Delete Admin'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminUserDetails
